<template>
	<client-only>
		<div v-show="showConsentPanel && cf" id="cookie-consent" class="popup popup-bottom" data-nosnippet>
			<div class="popup-row">
				<div class="popup-body" :class="currentStep">
					<div v-if="currentStep === 'consent'" id="cookie-consent-main" class="panel">
						<div class="logo">
							<img src="~/assets/images/logo-cafe-marcella-full.png" alt="Logo" />
						</div>
						<div class="content">
							<h3>{{ item.header }}</h3>
							<div v-parse-links v-html="item.content" />
						</div>
						<div class="buttons">
							<button class="button button-cta" @click="acceptAllCategories">
								{{ $t('consentAcceptAll') }}
							</button>
							<button class="button button-no-fill" @click="declineAllCategories">
								{{ $t('consentRejectAll') }}
							</button>
							<button class="button button-no-fill" @click="currentStep = 'settings'">
								{{ $t('consentSettings') }}
							</button>
						</div>
					</div>

					<!-- SETTINGS PANEL -->
					<div v-else-if="currentStep === 'settings'" id="cookie-consent-settings" class="panel">
						<div class="logo">
							<img src="~/assets/images/logo-cafe-marcella-full.png" alt="Logo" />
						</div>
						<div class="content">
							<h3>{{ item.headerConsentSettings }}</h3>

							<div class="consent-category">
								<input id="necessary" v-model="consent.necessary" type="checkbox" disabled />
								<h4>
									<label for="necessary">
										{{ $t('consentCategoryNecessary') }}
									</label>
								</h4>
								<div></div>
								<div class="category-content">
									<div v-parse-links v-html="item.contentNecessary" />
								</div>
							</div>

							<div class="consent-category">
								<input id="performance" v-model="consent.performance" type="checkbox" />
								<h4>
									<label for="performance">
										{{ $t('consentCategoryPerformance') }}
									</label>
								</h4>
								<div></div>
								<div class="category-content">
									<div v-parse-links v-html="item.contentPerformance" />
								</div>
							</div>

							<div class="consent-category">
								<input id="functional" v-model="consent.functional" type="checkbox" />
								<h4>
									<label for="functional">
										{{ $t('consentCategoryFunctional') }}
									</label>
								</h4>
								<div></div>
								<div class="category-content">
									<div v-parse-links v-html="item.contentFunctional" />
								</div>
							</div>

							<div class="consent-category">
								<input id="advertising" v-model="consent.advertising" type="checkbox" />
								<h4>
									<label for="advertising">
										{{ $t('consentCategoryAdvertising') }}
									</label>
								</h4>
								<div></div>
								<div class="category-content">
									<div v-parse-links v-html="item.contentAdvertising" />
								</div>
							</div>
						</div>

						<div class="buttons">
							<button class="button button-cta" @click="updateConsent">
								{{ $t('consentSaveAndClose') }}
							</button>
							<button class="button button-no-fill" @click="currentStep = 'consent'">
								{{ $t('back') }}
							</button>
						</div>
					</div>
				</div>
			</div>

			<div class="popup-background" />
		</div>
	</client-only>
</template>

<script setup>
const { fetchPage } = usePage();
const { openConsent, cfInit } = useCookieConsent();

await fetchPage();

defineProps({
	item: {
		type: Object,
		default: () => ({
			header: '',
			headerConsentSettings: '',
			content: '',
			contentNecessary: '',
			contentPerformance: '',
			contentFunctional: '',
			contentAdvertising: '',
		}),
	},
});

const showConsentPanel = useState('showConsentPanel', () => false);
const currentStep = ref('consent');
const cf = useState('cf', () => {});

const consent = reactive({
	necessary: true,
	performance: false,
	functional: false,
	advertising: false,
});

onMounted(() => {
	cfInit(initCookieFirst);
});

const initCookieFirst = () => {
	if (!window.CookieFirst) {
		return;
	}

	cf.value = window.CookieFirst;

	if (!cf.value.consent) {
		openConsent();
	}
};

const closeConsent = () => {
	showConsentPanel.value = false;
	document.body.classList.remove('locked');
};

const acceptAllCategories = () => {
	if (!cf.value) {
		return;
	}
	cf.value.acceptAllCategories();
	closeConsent();
};

const declineAllCategories = () => {
	if (!cf.value) {
		return;
	}
	cf.value.declineAllCategories();
	closeConsent();
};

const updateConsent = () => {
	if (!cf.value) {
		return;
	}
	cf.value.updateConsent({ ...consent });
	closeConsent();
};
</script>

<style lang="scss" scoped>
#cookie-consent {
	position: fixed;
	inset: 0;
	z-index: 99999999999;

	.popup-background {
		background: rgb(0 0 0 / 50%);
		position: absolute;
		inset: 0;
		z-index: 1;
	}

	.popup-row {
		transform: none;
		max-width: 100%;
		align-items: stretch;
		display: flex;
		flex-flow: row wrap;
		inset: auto 0 0;
		justify-content: space-between;
		max-height: 100%;
		overflow-y: auto;
		position: fixed;
		width: 100%;
		z-index: 2;
	}

	.popup-body {
		padding: 30px 60px;
		width: 100%;
		background: url('~/assets/images/body-bg-pattern-sm.jpg') no-repeat center center #d4d5d3;
		position: relative;
		box-sizing: border-box;
	}

	.panel {
		display: flex;
		gap: 50px;
	}

	.logo {
		max-width: 210px;
		display: flex;
		align-items: center;
	}

	.content {
		flex-basis: 60%;
		display: flex;
		justify-content: center;
		flex-direction: column;
		color: #5d3418;
	}

	.buttons {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}

	h3 {
		margin: 15px 0;
		font-size: 1.3em;
	}

	h4 {
		font-size: 1em;
	}

	p {
		font-size: 0.8em;
	}

	.button {
		min-width: 250px;
		appearance: none;
		background: #fff;
		margin-bottom: 10px;
		margin-right: 5px;
		padding: 6px 10px;
		font-size: 0.9em;
		border: 2px solid transparent;
		cursor: pointer;
		color: #5d3418;

		&:last-child {
			margin-bottom: 0;
		}

		&:hover {
			background: #5d3418;
			color: #fff;
			border: 2px solid #5d3418;
		}

		&.button-no-fill {
			background: transparent;
			border: 2px solid #fff;
			color: #fff;

			&:hover {
				background: #fff;
				border-color: #fff;
				color: #5d3418;
			}
		}
	}

	.consent-category {
		display: grid;
		grid-template-columns: 25px 1fr;
		gap: 5px 0;
		margin-bottom: 10px;

		input {
			max-width: 15px;
			display: flex;
			align-self: center;

			&:hover {
				cursor: pointer;

				&:disabled {
					cursor: not-allowed;
				}
			}
		}

		h4 {
			display: flex;
			align-self: center;
			margin-bottom: 0;
		}

		label:hover {
			cursor: pointer;
		}
	}
}

.language-navigation {
	position: absolute;
	inset: 10px 20px auto auto;
	z-index: 5;
	font-family: var(--heading-font-family);

	p {
		display: flex;
		flex-flow: column wrap;
	}

	a {
		width: 100%;
		text-decoration: none;
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		justify-content: flex-start;
		align-items: center;
		gap: 5px;
		font-weight: 400;
		margin: 3px 0;

		&::before {
			content: '';
			display: block;
			width: 13px;
			height: 13px;
			border: 1px solid #fff;
			border-radius: 50%;
			margin: -1px 0 1px;
		}

		&.router-link-exact-active::before {
			background: #fff;
		}
	}
}

@media (max-width: 1080px) {
	#cookie-consent {
		.panel {
			flex-direction: column;
			align-items: center;
			gap: 10px;
		}

		.content {
			flex-basis: 100%;
		}

		.logo {
			display: none;
		}

		.buttons {
			flex-flow: row wrap;
			justify-content: flex-start;
			gap: 0 10px;
			width: 100%;

			.button:last-child {
				margin-bottom: 10px;
			}
		}
	}
}

@media (max-width: 500px) {
	#cookie-consent {
		.popup-body {
			padding: 20px 30px;
		}

		.buttons {
			flex-direction: column;
			align-items: stretch;
		}

		.button {
			max-width: 250px;
			min-width: auto;
		}
	}
}
</style>
